import NetInfoLib from '@react-native-community/netinfo';
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Platform } from 'react-native';

import type { NetInfoState } from '@react-native-community/netinfo';

/**
 * App-level NetInfo provider. Holds ONE subscription to
 * @react-native-community/netinfo and fans out connectivity state to every
 * consumer via React context.
 *
 * Why this exists (TA-9366):
 *
 * The lib's internal `_subscriptions: Set<Listener>` is iterated with
 * `Set.forEach`. Each `useNetInfo()` callsite from the lib registers its
 * own subscriber. When network state changes and the lib invokes every
 * subscriber, any subscriber whose `setState` triggers a component remount
 * mid-iteration causes its `useNetInfo` hook to unsubscribe + resubscribe.
 * Per the JS Set spec, entries added during a `forEach` are also visited —
 * so the new subscriber fires inside the same dispatch, kicking another
 * setState, another render, another remount, until React's
 * max-update-depth guard trips (Sentry FRONTEND-2C9). Having many
 * `useNetInfo()` callsites in the app amplified the cascade.
 *
 * Collapsing to a single subscriber (this provider) removes the
 * remount-during-iteration window that drives the cascade. Consumers
 * subscribe to React context, which React batches and dispatches without
 * Set mutation hazards.
 *
 * Why the equality check below exists (TA-10639, Sentry FRONTEND-2Z9):
 *
 * Collapsing to one subscriber was necessary but not sufficient. The lib's
 * `State._handleNativeStateUpdate` (internal/state.ts:50) rebuilds the state
 * object from scratch on EVERY native connectivity event and broadcasts it
 * unconditionally — it never checks whether any value actually changed. On
 * web that event is `navigator.connection`'s `change`, which Chromium fires
 * every time it re-estimates downlink/rtt: on a flaky connection that is a
 * storm, ~15 events/second (the reported crash had ~100 breadcrumbs of
 * failing reachability HEADs at ~65ms intervals, right up to the throw).
 *
 * Passing React's raw `setState` as the subscriber meant every one of those
 * events stored a brand-new object. `Object.is(prevObject, nextObject)` is
 * always false for fresh objects, so React could never bail out, and the
 * entire tree under this provider re-rendered on each event. Consumers that
 * setState off `isConnected` (the NetInfoModal screens) then piled nested
 * updates on top until `getRootForUpdatedFiber` tripped React's
 * max-update-depth limit.
 *
 * Comparing by value and returning the previous object restores React's
 * bail-out: identical states become no-ops, and only genuine connectivity
 * transitions re-render.
 *
 * Why the transient-null carry-over exists (TA-10639, dev review):
 *
 * Value comparison alone does not close the ticket, because on a failing
 * connection `isInternetReachable` genuinely alternates instead of staying
 * put. Every native event runs `InternetReachability.update()` ->
 * `_setExpectsConnection(true)`, which cancels the in-flight HEAD and — since
 * `_isInternetReachable` is `false` — sets it to `null` ("check in flight")
 * and broadcasts; the restarted HEAD then fails and broadcasts `false` again.
 * Two real value changes per event, so the comparison correctly reports both
 * states as different and the whole tree re-renders anyway.
 *
 * `null` carries no new connectivity information: it means "we are asking",
 * not "reachability changed". Collapsing it onto the last known boolean turns
 * the false -> null -> false churn into a single stable state that the
 * comparison can then bail out on, while a real resolution (`true`/`false`)
 * still propagates immediately. The provider only ever reports `null` before
 * the first check resolves, which is exactly what `initialState` already says.
 */

/**
 * Whether an HTTP response from the reachability ping means the user is online.
 *
 * Why this exists (TA-11010, Sentry FRONTEND-2Z3 / 2ZB / 2ZD / 30D):
 *
 * On web the library defaults to `reachabilityUrl: '/'` with
 * `reachabilityTest: status === 200` — so the app health-checks itself by
 * sending a `HEAD` to its own origin (`https://app.tasktag.com/`), through the
 * same CDN and rate limiter that serves real traffic. In production that ping
 * is coming back 429 and 502 for real users.
 *
 * Under the default test that is graded "not reachable", which is wrong twice
 * over:
 *
 * 1. It is a false offline verdict. A 429 or a 502 is a *reply* — the request
 *    reached our origin, so the user's network is fine. On web the only reader
 *    of `isInternetReachable` today is `useApolloClient`'s Sentry
 *    `connectionInfo`, which then tags every concurrent failure as having
 *    happened offline. (`ChatMessagesUnavailable` also reads it, but only the
 *    native ChatMessageList renders that component.) Any web UI that later
 *    starts reading the flag would inherit the wrong verdict.
 * 2. It sustains the rate limit. `InternetReachability` schedules the next
 *    check with `reachabilityShortTimeout` (5s) after a failure and
 *    `reachabilityLongTimeout` (60s) after a success, so the first 429 drops
 *    every affected tab to a 5-second poll against the endpoint that is
 *    already refusing it — twelve times the steady-state rate, for as long as
 *    the limit holds.
 *
 * Any HTTP response therefore counts as reachable. Genuine loss of
 * connectivity does not reach this callback at all: the `fetch` rejects, or
 * the 15s `reachabilityRequestTimeout` fires, and the library's own catch
 * branch reports unreachable and retries on the short timeout — which is the
 * behaviour we want there, since a failing local fetch puts no load on the
 * origin.
 *
 * Status `0` (an opaque or network-error-shaped response) is not a reply and
 * stays unreachable.
 *
 * Note this only re-grades the ping. Whether the origin should be rate-limiting
 * `HEAD /` at all is a backend/CDN question — see TA-11010 and TA-10770.
 */
export const isReachableHttpResponse = (
  response: Pick<Response, 'status'>
): boolean => response.status > 0;

// The web default (`reachabilityUrl: '/'`, `reachabilityTest: status === 200`)
// is the one being corrected — native uses a different URL and a 204 test, and
// its reachability comes from the OS, so it is left alone. `configure` tears
// down the library's state and drops every existing listener, so it has to run
// before NetInfoProvider subscribes below; module scope is early enough, and
// this file is the app's single owner of the library (TA-9366).
if (Platform.OS === 'web') {
  NetInfoLib.configure({
    reachabilityTest: (response) =>
      Promise.resolve(isReachableHttpResponse(response)),
  });
}

// Compares one level of an object by value. Used for `details`, whose web
// shape ({ isConnectionExpensive, cellularGeneration, carrier }) is rebuilt
// on every event and so is never reference-equal.
const shallowEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;

  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const leftKeys = Object.keys(left);

  if (leftKeys.length !== Object.keys(right).length) return false;
  // hasOwnProperty, not just Object.is: with equal key counts but different
  // key sets, a missing key reads back as undefined and would compare equal
  // to an explicit undefined on the other side (Copilot review).
  return leftKeys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(right, key) &&
      Object.is(left[key], right[key])
  );
};

/**
 * True when two NetInfo states carry the same connectivity information.
 *
 * Compares every top-level key rather than an explicit allowlist so that
 * platform-specific fields (`isWifiEnabled` on Android, added by the lib
 * outside the common `NetInfoState` shape) are covered without this needing
 * to track the lib's type union. `details` is the one nested object, so it
 * gets one extra level of value comparison.
 */
export const areNetInfoStatesEqual = (
  a: NetInfoState,
  b: NetInfoState
): boolean => {
  if (Object.is(a, b)) return true;
  if (!a || !b) return false;

  const left = a as unknown as Record<string, unknown>;
  const right = b as unknown as Record<string, unknown>;
  const leftKeys = Object.keys(left);

  if (leftKeys.length !== Object.keys(right).length) return false;

  // The hasOwnProperty guard pins the key sets together — see shallowEqual.
  return leftKeys.every((key) => {
    if (!Object.prototype.hasOwnProperty.call(right, key)) return false;
    return key === 'details'
      ? shallowEqual(left[key], right[key])
      : Object.is(left[key], right[key]);
  });
};

/**
 * Collapses a transient `isInternetReachable: null` onto the previous known
 * boolean, so a reachability check that is merely in flight does not read as a
 * connectivity change. Returns `next` untouched in every other case.
 *
 * Exported for direct testing — the storm this guards against cannot be
 * reproduced through the provider without a real failing network.
 */
export const carryTransientReachability = (
  previous: NetInfoState,
  next: NetInfoState
): NetInfoState =>
  next.isInternetReachable === null &&
  typeof previous.isInternetReachable === 'boolean'
    ? ({
        ...next,
        isInternetReachable: previous.isInternetReachable,
      } as NetInfoState)
    : next;

// Use the string literal rather than the lib's NetInfoStateType enum, which
// isn't exposed as a named export by the jest mock and would crash test
// imports of any module that pulls this in.
const initialState: NetInfoState = {
  type: 'unknown',
  isConnected: null,
  isInternetReachable: null,
  details: null,
} as NetInfoState;

const NetInfoContext = createContext<NetInfoState>(initialState);

export const NetInfoProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<NetInfoState>(initialState);
  useEffect(() => {
    // Returning `previous` unchanged is what lets React bail out of the
    // render — see the TA-10639 note above. Do not pass `setState` directly.
    return NetInfoLib.addEventListener((next: NetInfoState) =>
      setState((previous) => {
        const merged = carryTransientReachability(previous, next);
        return areNetInfoStatesEqual(previous, merged) ? previous : merged;
      })
    );
  }, []);
  return (
    <NetInfoContext.Provider value={state}>{children}</NetInfoContext.Provider>
  );
};

export const useNetInfo = (): NetInfoState => useContext(NetInfoContext);

/**
 * Runs `onReconnect` once each time connectivity goes offline -> online,
 * reading the shared provider's state rather than registering another
 * `NetInfoLib.addEventListener` subscriber.
 *
 * Why this exists (TA-10662):
 *
 * Reconnect-handling features (catch-up message fetch, push-token
 * re-registration) each independently reached for the library's
 * `addEventListener`, which reopens the multi-subscriber re-entrancy window
 * TA-9366 closed — see the note at the top of this file. The handlers do not
 * have to `setState` themselves to be harmful: every extra subscriber widens
 * the `Set.forEach` window that drives the cascade. This hook is the
 * supported way to do reconnect work; new call sites should use it instead of
 * subscribing to the library directly.
 *
 * `onReconnect` is read through a ref, so an inline callback does not
 * re-arm the effect on every render.
 */
export const useOnReconnect = (onReconnect: () => void): void => {
  const { isConnected } = useNetInfo();
  const wasOfflineRef = useRef(false);
  const onReconnectRef = useRef(onReconnect);
  onReconnectRef.current = onReconnect;

  useEffect(() => {
    // `null` means NetInfo has not resolved its first check yet — undetermined,
    // not offline. Recording it as offline would make the first resolved
    // `true` read as an offline -> online transition and fire `onReconnect`
    // on every mount, which a raw subscription never did.
    if (isConnected === null) return;
    if (wasOfflineRef.current && isConnected) onReconnectRef.current();
    wasOfflineRef.current = !isConnected;
  }, [isConnected]);
};
